import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Collapse,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material";
import { FormProvider, useForm, useFormContext, useFormState, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import HistoryIcon from "@mui/icons-material/History";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  colors,
  fontSize,
  fontWeight,
  padding,
  gaps,
  borderRadius,
  margin,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import TextInputField from "controls/global/text-input-field";
import SelectField from "controls/global/select-field";
import DatePickerField from "controls/global/datepicker";
import NumberInputFieldPricing from "controls/global/number-input-field/pricing/NumberInputFieldPricing";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { formatDate } from "../shared/policyColumnHelpers";
import { AdjustmentKind, BookedRow, HOLD_SCOPE, PERMISSIONS, PIPActionType, StageType } from "utils/data/enum";
import { forcePromoteRecords, removeForcePromoteRecord } from "../shared/forcePromote";
import {
  ForcePromoteModal,
  TransferModal,
  AdjustmentModal,
  OverrideFinancialsModal,
} from "../modals";
import EditRecordModal from "../product-details/EditRecordModal";
import { financialsDisplaySource } from "../modals/helper";
import BulkActionModal from "../shared/BulkActionModal";
import { holdPolicyRecord, removeHoldPolicyRecord } from "../shared/hold";
import { deletePolicyRecord } from "../shared/delete";
import { handleBulkReturnPolicyTransaction } from "../shared/returnQueue";
import {
  beginLockSession,
  endLockSession,
  forceReleaseLockSession,
  lockPolicyRecord,
  unlockPolicyRecordSilently,
} from "../shared/lock";
import LockedBanner, { relativeTime } from "../shared/LockedBanner";
import { EDIT_FIELD_BY_KEY } from "../product-details/editFields";
import { bulkEditPolicyTransaction } from "utils/api/policyTransactionApi";
import type { PolicyTransactionLockResultDto } from "utils/api/policyTransactionApi";
import { getPropertyTypeList } from "utils/api/lookupApi";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { showToast } from "controls/global/stewart-toast";
import { EditPolicyHistoryDto, savePolicyHistory } from "utils/api/policyHistoryApi";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import useStateList from "utils/custom-hooks/useStateList";
import { useTransCodeOptions } from "utils/custom-hooks/useTransCodeOptions";
import { useTransactionTypeOptions } from "utils/custom-hooks/useTransactionTypeOptions";
import { useCountyList } from "utils/custom-hooks/useCountyList";

// ----- styled containers -----

const PanelContainer = styled(Box)({
  padding: `${padding.medium1} ${padding.large1}`,
  backgroundColor: colors.grey10,
  borderTop: `1px solid ${colors.grey09}`,
});

// Caps the drawer content width on ultra-wide displays. The grey background
// supplied by PanelContainer still fills the entire row; only the inner
// content (header + form sections + action row) is bounded and centered.
const PanelContent = styled(Box)({
  maxWidth: 1400,
  marginLeft: 0,
});

const FormFieldScope = styled(Box)({
  "& .MuiInputLabel-root[data-shrink='false']": {
    top: "-5px !important",
  },
  // Clip the disabled grey fill to the rounded border so it doesn't spill past
  // the corners on text/dropdown fields (matches the date fields).
  "& .MuiInputBase-root.Mui-disabled": {
    overflow: "hidden",
  },
});

const PanelTextField = (props: React.ComponentProps<typeof TextInputField>) => (
  <TextInputField
    shrinkLabelOnError={false}
    {...props}
  />
);

const PanelSelectField = (props: React.ComponentProps<typeof SelectField>) => (
  <SelectField
    shrinkLabelOnError={false}
    {...props}
  />
);

const Section = styled(Box)({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  padding: padding.medium1,
  marginBottom: gaps.medium1,
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  // Wrap on narrow screens so the Booked selector/chips drop below the title
  // instead of overflowing off-screen.
  flexWrap: "wrap",
  columnGap: gaps.medium1,
  rowGap: gaps.small1,
  marginBottom: gaps.small1,
  paddingBottom: gaps.xsmall2,
  borderBottom: `1px solid ${colors.grey09}`,
});

const SectionTitle = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const Grid4 = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  columnGap: gaps.medium1,
  rowGap: gaps.large1,
  padding: `${padding.small1} 0`,
  "@media (max-width: 1200px)": {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  "@media (max-width: 900px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

const Grid5 = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  columnGap: gaps.medium1,
  rowGap: gaps.large1,
  padding: `${padding.small1} 0`,
  "@media (max-width: 1400px)": {
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  },
  "@media (max-width: 1100px)": {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  "@media (max-width: 800px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "minmax(0, 1fr)",
  },
});

// Trans Code 1-8 group. Each cell holds a Trans Code + Amount pair (see
// TransCodePair), so auto-fit wraps by whole pairs — a code and its amount are
// never split across rows — down to a single pair per row on small devices.
const Grid8 = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  // Wider row gap so a field's absolutely-positioned validation message (a
  // single clamped line) clears the row beneath it instead of overlapping the
  // next field.
  columnGap: gaps.medium1,
  rowGap: gaps.large1,
  padding: `${padding.small1} 0`,
});

// A Trans Code + its Rate Amount, kept side by side as one unit.
const TransCodePair = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: gaps.small1,
});

const FieldCellRoot = styled(Box)({
  position: "relative",
  "& .errorMessage": {
    left: 0,
    right: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

// Form fields the server flagged on load (record.PolicyErrors). A flagged field
// is force-enabled by FieldCell so the reviewer can correct it in place, even
// where the layout would normally lock it. The set is sticky for the life of the
// open row — it is not derived from the live error state — so the field doesn't
// re-disable itself the moment the message clears mid-keystroke.
const ServerErrorFieldsContext = React.createContext<ReadonlySet<string>>(
  new Set()
);

const FieldCell = ({ children }: { children: React.ReactElement }) => {
  const child = React.Children.only(children);
  const childProps = child.props as { name?: string; errorOverride?: string };
  const name = childProps.name;
  const { control } = useFormContext();
  const { errors } = useFormState({ control });
  const message =
    (name ? (errors as Record<string, any>)[name]?.message : undefined) ||
    childProps.errorOverride ||
    "";
  const unlocked = React.useContext(ServerErrorFieldsContext).has(name ?? "");
  return (
    <Tooltip title={message} arrow disableHoverListener={!message}>
      <FieldCellRoot>
        {unlocked
          ? React.cloneElement(child, { disabled: false } as any)
          : children}
      </FieldCellRoot>
    </Tooltip>
  );
};

const SubSection = styled(Box)({
  borderRadius: borderRadius.small,
  padding: padding.small1,
  marginTop: gaps.small1,
  border: `1px solid transparent`,
  "&.booked": {
    backgroundColor: colors.blue08,
    borderLeft: `3px solid ${colors.blue01}`,
    border: `1px solid ${colors.blue01}`,
  },
});

const SubSectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const BookedPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "3px",
  fontSize: fontSize.xxsmall,
  fontWeight: fontWeight.bold1,
  padding: `2px ${padding.small1}`,
  borderRadius: borderRadius.xlarge,
  color: colors.white,
  backgroundColor: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

// Muted, normal-weight caption rendered next to a subsection title
// (e.g. "Calculated  Sourced from STRAFE").
const SubSectionCaption = styled(Box)({
  fontSize: fontSize.xsmall,
  fontWeight: 400,
  color: colors.grey11,
  textTransform: "none",
  letterSpacing: 0,
});

const BookedSelector = styled(Box)({
  display: "flex",
  alignItems: "center",
  // Allow the label, radios and chips to wrap on very narrow screens.
  flexWrap: "wrap",
  gap: gaps.small1,
  marginLeft: "auto",
  marginRight: "auto",
});

const BookedLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold1,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
});

const ActionRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: gaps.small1,
  marginTop: gaps.medium1,
});

// Close-Queue-only read-only banner. Hex literals match the mockup
// (adam_newest_v3 1.html lines 6984-6989) — amber background + border.
const CqBanner = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  marginTop: gaps.medium1,
  backgroundColor: "#fff8e5",
  border: "1px solid #e8c068",
  borderRadius: borderRadius.small,
  padding: `${padding.small1} ${padding.medium1}`,
  color: "#7a5a00",
});

const HistCleanBanner = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  marginBottom: gaps.medium1,
  backgroundColor: colors.blue23,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.small,
  padding: `${padding.small1} ${padding.medium1}`,
  color: colors.grey26,
});

const AdjustmentInfoBanner = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
  marginBottom: gaps.medium1,
  backgroundColor: colors.yellow04,
  border: `1px solid ${colors.yellow02}`,
  borderRadius: borderRadius.small,
  padding: `${padding.medium1} ${padding.medium1}`,
  color: colors.yellow01,
});

const AdjustmentInfoBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
  flex: 1,
  fontSize: fontSize.small,
});

const EditedAfterCloseBanner = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.small1,
  marginBottom: gaps.medium1,
  backgroundColor: colors.white02,
  border: `1px solid ${colors.yellow09}`,
  borderRadius: borderRadius.small,
  padding: `${padding.medium1} ${padding.medium1}`,
  color: colors.yellow01,
});

const EditedAfterCloseHeader = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: gaps.small1,
});

const EditedAfterCloseBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
  flex: 1,
  fontSize: fontSize.small,
});

const EditedFieldsList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
  border: `1px solid ${colors.yellow09}`,
  paddingTop: padding.small1,
});

const EditedFieldRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: gaps.large1,
  fontSize: fontSize.small,
});

const EDITED_FIELD_LABELS: Record<string, string> = {
  CountyCode: "County Code",
  MunicipalityNum: "Municipal Code",
  PropertyTypeCode: "Property Type",
};

const editedFieldLabel = (fieldName: string) =>
  EDITED_FIELD_LABELS[fieldName] ?? fieldName;

const OVERRIDE_INPUT_STYLES = {
  backgroundColor: colors.yellow08,
  border: `1px solid ${colors.yellow09}`,
  borderRadius: borderRadius.small,
  color: colors.yellow10,
  fontWeight: fontWeight.bold2,
};

const overrideTip = (
  record: PolicyManagementRecord,
  bookedRow: BookedRow,
  formField: string,
  side: BookedRow
): string | null => {
  if (!record.IsOverride || bookedRow !== side) return null;
  const wasByField: Record<string, number | null | undefined> =
    side === BookedRow.REPORTED
      ? {
          reportedPremium: record.PreOverrideActualFee,
          reportedUwRemittance: record.PreOverrideActualRemittance,
          reportedTax: record.PreOverrideActualPremiumTax,
        }
      : {
          calculatedFee: record.CalculatedFee,
          calculatedUwRemittance: record.CalculatedRemittance,
          calculatedTax: record.CalculatedPremiumTax,
        };
  if (!(formField in wasByField)) return null;
  const was = wasByField[formField];
  const wasStr =
    was != null
      ? `$${Number(was).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
      : "—";
  const by = record.OverrideByUsername || "—";
  const on = record.OverrideDate ? formatDate(record.OverrideDate) : "—";
  return `Was: ${wasStr}\nOverridden by ${by}\nOn ${on}`;
};

const FinField = ({
  record,
  bookedRow,
  name,
  label,
  side,
  disabled,
}: {
  record: PolicyManagementRecord;
  bookedRow: BookedRow;
  name: string;
  label: string;
  side: BookedRow;
  disabled?: boolean;
}) => {
  const tip = overrideTip(record, bookedRow, name, side);
  const field = (
    <NumberInputFieldPricing
      name={name}
      label={label}
      disabled={disabled ?? true}
      styles={tip ? OVERRIDE_INPUT_STYLES : {}}
    />
  );
  if (!tip) return field;
  return (
    <Tooltip
      arrow
      placement="top"
      title={<Box sx={{ whiteSpace: "pre-line" }}>{tip}</Box>}
    >
      <Box>{field}</Box>
    </Tooltip>
  );
};

// ----- dropdown option lists (from HTML mockup adam_newest_v3 1.html) -----

type Opt = { value: string; text: string };

const SELECT_PLACEHOLDER: Opt = { value: "", text: "- Select -" };

const resolveOptionValue = (value: unknown, options: Opt[]) => {
  const raw = value == null ? "" : String(value);
  if (!raw || options.some((o) => o.value === raw)) return raw;
  const key = raw.trim().toLowerCase();
  const match = options.find(
    (o) => o.value !== "" && (o.text ?? "").trim().toLowerCase() === key
  );
  return match ? match.value : raw;
};

const optionsWithValue = (options: Opt[], value: unknown): Opt[] => {
  const raw = value == null ? "" : String(value);
  if (!raw || options.some((o) => o.value === raw)) return options;
  return [...options, { value: raw, text: raw }];
};

// ----- form values shape + record <-> form mapping -----

type FormValues = {
  // Policy Identity
  policyNumber: string;
  fileNumber: string;
  reportingId: string;
  agentID: string;
  policyFormName: string;
  productName: string;
  originalPolicyNumber: string;
  originalPolicyDate: Date | null;
  originalPolicyType: string;
  agentName: string;
  municipalityNum: string;
  city: string;
  zip: string;
  stateCode: string;
  effectiveDate: Date | null;
  issueDate: Date | null;
  receivedDate: Date | null;
  voidedDate: Date | null;
  // Policy Identity — Product Details layout (read-only view)
  policyType: string;
  formPrefix: string;
  serialNumber: string;
  status: string;
  county: string;
  countyCode: string;
  source: string;
  legacyIdLocation: string;
  legacyIdCompany: string;
  batchNumber: string;
  // Transaction
  transCode: string;
  transCode2: string;
  transCode3: string;
  transCode4: string;
  transCode5: string;
  transCode6: string;
  transCode7: string;
  transCode8: string;
  rateAmount: number;
  rateAmount2: number;
  rateAmount3: number;
  rateAmount4: number;
  rateAmount5: number;
  rateAmount6: number;
  rateAmount7: number;
  rateAmount8: number;
  transactionType: string;
  recordTypeCode: string;
  propertyTypeCode: string;
  // Transaction — Product Details layout (read-only view)
  ticketNumber: string;
  assignedDate: Date | null;
  modifiedDate: Date | null;
  // Financials toggle
  bookedRow: BookedRow;
  // Reported
  reportedPremium: number;
  reportedNameTbd: number;
  reportedRetention: number;
  reportedTax: number;
  reportedRiskRate: number;
  reportedUwRemittance: number;
  // Calculated
  liabilityAmount: number;
  calculatedFee: number;
  calculatedNameTbd: number;
  actualRetention: number;
  calculatedTax: number;
  calculatedRiskRate: number;
  calculatedUwRemittance: number;
  // Other
  originalPolicyAmount: number;
  reissueLiabilityAmount: number;
  siLiabilityAmount: number;
  addlLiabilityAmount: number;
};

const toDate = (v?: string): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const fromDate = (v: Date | null | undefined): string =>
  v && !isNaN(v.getTime()) ? v.toISOString() : "";

// Compare two form values the way these controls actually store them: dates are
// `Date | null`, and selects/text inputs clear to "" rather than null, so a raw
// `===` would report a change where the reviewer made none.
const normalizeFieldValue = (v: unknown) =>
  v instanceof Date
    ? isNaN(v.getTime())
      ? ""
      : v.getTime()
    : v == null || v === ""
      ? ""
      : v;

const isSameFieldValue = (a: unknown, b: unknown) =>
  normalizeFieldValue(a) === normalizeFieldValue(b);

const toFormValues = (r: PolicyManagementRecord): FormValues => {
  const bookedRow =
    r.BookedTypeCode === BookedRow.REPORTED
      ? BookedRow.REPORTED
      : BookedRow.CALCULATED;
  const financials = financialsDisplaySource(r, bookedRow);

  return {
  policyNumber: r.PolicyNumber ?? "",
  fileNumber: r.FileNumber ?? "",
  reportingId: r.reportingId ?? "",
  agentID: r.AgentID ?? "",
  policyFormName: r.PolicyFormName ?? "",
  productName: r.ProductTypeAbbrTypeName ?? "",
  originalPolicyNumber: r.OriginalPolicyNumber ?? "",
  originalPolicyDate: toDate(r.OriginalPolicyDate ?? undefined),
  originalPolicyType: r.originalPolicyType ?? "",
  agentName: r.LocationDisplayName ?? "",
  municipalityNum: r.MunicipalityNum ?? "",
  city: r.PropertyCity ?? "",
  zip: r.PropertyZipCode ?? "",
  stateCode: r.StateCode ?? "",
  effectiveDate: toDate(r.EffectiveDate ?? undefined),
  issueDate: toDate(r.issueDate),
  receivedDate: toDate(r.ReceivedDate ?? undefined),
  voidedDate: toDate(r.VoidedDate ?? undefined),
  // Product Details (read-only) — Policy Identity extras
  policyType: `${r.ProductTypeAbbrTypeCode ?? ""} - ${r.ProductTypeAbbrTypeName ?? ""}`,
  formPrefix: r.FormPrefix ?? "",
  // Serial number is the trailing segment of the full policy number.
  serialNumber: (r.PolicyNumber ?? "").split("-").pop() ?? "",
  status: r.StageTypeCode ?? "",
  county: ((r.CountyDisplayName ?? "").split(" - ")[1] ?? "").trim(),
  countyCode: ((r.CountyDisplayName ?? "").split(" - ")[0] ?? "").trim(),
  source: r.OriginationSource ?? "",
  legacyIdLocation: "",
  legacyIdCompany: "",
  batchNumber: r.BatchNumber ?? "",
  transCode: r.TransCode ?? "",
  transCode2: r.TransCode2 ?? "",
  transCode3: r.TransCode3 ?? "",
  transCode4: r.TransCode4 ?? "",
  transCode5: r.TransCode5 ?? "",
  transCode6: r.TransCode6 ?? "",
  transCode7: r.TransCode7 ?? "",
  transCode8: r.TransCode8 ?? "",
  rateAmount: r.RateAmount ?? 0,
  rateAmount2: r.RateAmount2 ?? 0,
  rateAmount3: r.RateAmount3 ?? 0,
  rateAmount4: r.RateAmount4 ?? 0,
  rateAmount5: r.RateAmount5 ?? 0,
  rateAmount6: r.RateAmount6 ?? 0,
  rateAmount7: r.RateAmount7 ?? 0,
  rateAmount8: r.RateAmount8 ?? 0,
  transactionType: r.TransactionTypeCode ?? "",
  recordTypeCode: r.RecordTypeCode ?? "",
  propertyTypeCode: r.PropertyTypeCode ?? "",
  // Product Details (read-only) — Transaction extras
  ticketNumber: "",
  assignedDate: null,
  modifiedDate: toDate(r.LastEditedDate ?? undefined),
  bookedRow,
  // Reported row: Actual*, unless Calculated is booked and overridden — then
  // the override moved to Actual* on the Calculated side, so Reported instead
  // shows the frozen PreOverride snapshot.
  reportedPremium: financials.reported.fee ?? 0,
  reportedNameTbd: r.reportedNameTbd ?? 0,
  reportedRetention: r.reportedRetention ?? 0,
  reportedTax: financials.reported.tax ?? 0,
  reportedRiskRate: r.ActualRiskRate ?? 0,
  reportedUwRemittance: financials.reported.remittance ?? 0,
  // Calculated row: Calculated*, unless Calculated is booked and overridden —
  // then it shows the overridden Actual* values instead.
  liabilityAmount: r.LiabilityAmount ?? 0,
  calculatedFee: financials.calculated.fee ?? 0,
  calculatedNameTbd: r.calculatedNameTbd ?? 0,
  actualRetention: r.ActualRetention ?? 0,
  calculatedTax: financials.calculated.tax ?? 0,
  calculatedRiskRate: r.CalculatedRiskRate ?? 0,
  calculatedUwRemittance: financials.calculated.remittance ?? 0,
  originalPolicyAmount: r.originalPolicyAmount ?? 0,
  reissueLiabilityAmount: r.ReissueLiabilityAmount ?? 0,
  siLiabilityAmount: r.SILiabilityAmount ?? 0,
  addlLiabilityAmount: r.AdditionalLiabilityAmount ?? 0,
  };
};

const mergeBack = (
  record: PolicyManagementRecord,
  values: Partial<FormValues>
): PolicyManagementRecord => ({
  ...record,
  PolicyNumber: values.policyNumber ?? record.PolicyNumber,
  FileNumber: values.fileNumber ?? record.FileNumber,
  reportingId: values.reportingId ?? record.reportingId,
  AgentID: values.agentID ?? record.AgentID,
  PolicyFormName: values.policyFormName ?? record.PolicyFormName,
  ProductTypeAbbrTypeName: values.productName ?? record.ProductTypeAbbrTypeName,
  originalPolicyNumber: values.originalPolicyNumber ?? record.originalPolicyNumber,
  originalPolicyDate: "originalPolicyDate" in values
    ? fromDate(values.originalPolicyDate)
    : record.originalPolicyDate,
  originalPolicyType: values.originalPolicyType ?? record.originalPolicyType,
  agentName: values.agentName ?? record.agentName,
  MunicipalityNum: values.municipalityNum ?? record.MunicipalityNum,
  PropertyCity: values.city ?? record.PropertyCity,
  PropertyZipCode: values.zip ?? record.PropertyZipCode,
  StateCode: values.stateCode ?? record.StateCode,
  CountyCode: values.countyCode ?? record.CountyCode,
  CountyDisplayName: record.CountyDisplayName,
  effectiveDate: "effectiveDate" in values
    ? fromDate(values.effectiveDate)
    : record.effectiveDate,
  issueDate: "issueDate" in values ? fromDate(values.issueDate) : record.issueDate,
  ReceivedDate: "receivedDate" in values
    ? fromDate(values.receivedDate)
    : record.ReceivedDate,
  voidedDate: "voidedDate" in values ? fromDate(values.voidedDate) : record.voidedDate,
  TransCode: values.transCode ?? record.TransCode,
  TransCode2: values.transCode2 ?? record.TransCode2,
  TransCode3: values.transCode3 ?? record.TransCode3,
  TransCode4: values.transCode4 ?? record.TransCode4,
  TransCode5: values.transCode5 ?? record.TransCode5,
  TransCode6: values.transCode6 ?? record.TransCode6,
  TransCode7: values.transCode7 ?? record.TransCode7,
  TransCode8: values.transCode8 ?? record.TransCode8,
  RateAmount: values.rateAmount ?? record.RateAmount,
  RateAmount2: values.rateAmount2 ?? record.RateAmount2,
  RateAmount3: values.rateAmount3 ?? record.RateAmount3,
  RateAmount4: values.rateAmount4 ?? record.RateAmount4,
  RateAmount5: values.rateAmount5 ?? record.RateAmount5,
  RateAmount6: values.rateAmount6 ?? record.RateAmount6,
  RateAmount7: values.rateAmount7 ?? record.RateAmount7,
  RateAmount8: values.rateAmount8 ?? record.RateAmount8,
  TransactionTypeCode: values.transactionType ?? record.TransactionTypeCode,
  RecordTypeCode: values.recordTypeCode ?? record.RecordTypeCode,
  PropertyTypeCode: values.propertyTypeCode ?? record.PropertyTypeCode,
  BookedTypeCode: values.bookedRow ?? record.bookedRow,
  reportedPremium: values.reportedPremium ?? record.reportedPremium,
  reportedNameTbd: values.reportedNameTbd ?? record.reportedNameTbd,
  reportedRetention: values.reportedRetention ?? record.reportedRetention,
  reportedTax: values.reportedTax ?? record.reportedTax,
  LiabilityAmount: values.liabilityAmount ?? record.LiabilityAmount,
  calculatedNameTbd: values.calculatedNameTbd ?? record.calculatedNameTbd,
  ActualRetention: values.actualRetention ?? record.ActualRetention,
  calculatedTax: values.calculatedTax ?? record.calculatedTax,
  originalPolicyAmount: values.originalPolicyAmount ?? record.originalPolicyAmount,
  ReissueLiabilityAmount: values.reissueLiabilityAmount ?? record.ReissueLiabilityAmount,
  SILiabilityAmount: values.siLiabilityAmount ?? record.SILiabilityAmount,
  AdditionalLiabilityAmount: values.addlLiabilityAmount ?? record.AdditionalLiabilityAmount,
});

// ----- yup schema -----

// "This value is missing" is the server's call, not the client's: those messages
// arrive per field as record.PolicyErrors. Only format rules live here, so a
// cleared server error isn't immediately replaced by a local "Required" (which
// would keep Re-run Validation disabled).
const policyDetailsSchema = yup.object({
  policyNumber: yup.string().trim().nullable(),
  fileNumber: yup.string().trim().nullable(),
  reportingId: yup.string().nullable(),
  agentID: yup.string().trim().nullable(),
  policyFormName: yup.string().nullable(),
  productName: yup.string().nullable(),
  originalPolicyNumber: yup.string().nullable(),
  originalPolicyDate: yup.date().nullable().typeError("Invalid date"),
  originalPolicyType: yup.string().nullable(),
  agentName: yup.string().nullable(),
  municipalityNum: yup.string().nullable(),
  city: yup.string().nullable(),
  zip: yup.string().nullable(),
  stateCode: yup.string().nullable(),
  countyCode: yup.string().nullable(),
  effectiveDate: yup.date().nullable().typeError("Invalid date"),
  issueDate: yup.date().nullable().typeError("Invalid date"),
  receivedDate: yup.date().nullable().typeError("Invalid date"),
  voidedDate: yup.date().nullable().typeError("Invalid date"),
  transCode: yup.string().nullable(),
  transCode2: yup.string().nullable(),
  transCode3: yup.string().nullable(),
  transCode4: yup.string().nullable(),
  transCode5: yup.string().nullable(),
  transCode6: yup.string().nullable(),
  transCode7: yup.string().nullable(),
  transCode8: yup.string().nullable(),
  rateAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount2: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount3: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount4: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount5: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount6: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount7: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  rateAmount8: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  transactionType: yup.string().nullable(),
  recordTypeCode: yup.string().nullable(),
  propertyTypeCode: yup.string().nullable(),
  bookedRow: yup.string().oneOf([BookedRow.CALCULATED, BookedRow.REPORTED]),
  reportedPremium: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  reportedNameTbd: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  reportedRetention: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  reportedTax: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  liabilityAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  calculatedFee: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  calculatedNameTbd: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  actualRetention: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  calculatedTax: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  originalPolicyAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  reissueLiabilityAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  siLiabilityAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
  addlLiabilityAmount: yup.number().min(0, "Must be ≥ 0").typeError("Must be a number"),
});

// ----- per-field server error mapping -----

// A server validation error bound to the value that produced it. The message is
// shown for exactly as long as the field still holds `rejectedValue`, so merely
// focusing and blurring the field can never clear it.
type ServerFieldError = {
  name: keyof FormValues;
  message: string;
  rejectedValue: unknown;
};

// API ErrorFieldName values whose camelCased form doesn't match the form field
// name (e.g. the API reports "CountyCodeNum" for the "countyCode" field). These
// aliases are checked before the generic camelCase fallback below.
const ERROR_FIELD_NAME_ALIASES: Record<string, keyof FormValues> = {
  CountyCodeNum: "countyCode",
};

// Resolve an API ErrorFieldName (PascalCase, e.g. "CountyCode",
// "MunicipalityNum") to its camelCased form field name.
const resolveErrorFieldName = (
  apiName: string,
  validNames: Set<string>
): keyof FormValues | undefined => {
  const alias = ERROR_FIELD_NAME_ALIASES[apiName];
  if (alias && validNames.has(alias)) return alias;
  const camel = apiName.charAt(0).toLowerCase() + apiName.slice(1);
  return validNames.has(camel) ? (camel as keyof FormValues) : undefined;
};


// ----- status badge (unchanged from prior implementation) -----

function getStatusBadge(record: PolicyManagementRecord) {
  if (record.isClosed)
    return { label: "Completed", bg: "#dcfce7", color: "#166534", border: "#86efac" };
  if (record.IsOnHold && record.isReadyToClose)
    return { label: "Close Queue — On Hold", bg: "#ffedd5", color: "#9a3412", border: "#fdba74" };
  if (record.isReadyToClose)
    return { label: "Close Queue", bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" };
  if (record.IsOnHold && record.isReadyToPreClose)
    return { label: "Pre-Close — On Hold", bg: "#ffedd5", color: "#9a3412", border: "#fdba74" };
  if (record.isReadyToPreClose)
    return { label: "Pre-Close", bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };
  if (record.IsOnHold)
    return { label: "Quality Review — On Hold", bg: "#ffedd5", color: "#9a3412", border: "#fdba74" };
  return { label: "Quality Review", bg: "#fce8e8", color: "#c62828", border: "#fca5a5" };
}

// ----- component -----

interface PolicyDetailsPanelProps {
  record: PolicyManagementRecord;
  editable?: boolean;
  onRecordChange?: (next: PolicyManagementRecord) => void;
  onRefresh?: () => void;
  onCollapse?: () => void;
  variant?: "qr" | "pcq" | "cq" | "history";
  hideActions?: boolean;
}

const PolicyDetailsPanel = ({
  record,
  editable = false,
  onRecordChange,
  onRefresh,
  onCollapse,
  variant = "qr",
  hideActions = false,
}: PolicyDetailsPanelProps) => {
  // When the row is read-only (history / pre-close / close-queue), every
  // input is disabled. When editable, per-field "not editable" overrides
  // are applied via explicit `disabled` props below.
  const isReadOnly = !editable;
  const validationLocked = variant === "qr" || variant === "pcq";
  // Pre-Close only: a record with no open errors doesn't need the force flag —
  // moving it to the Close Queue is an ordinary promotion, so the action is
  // relabelled "Promote" and sent with IsForcePromoted = 0. Anything with open
  // errors, and the whole Quality Review drawer, keeps Force Promote.
  const isPlainPromote = variant === "pcq" && (record.OpenErrorCount ?? 0) === 0;
  const navigate = useNavigate();
  const [{ profileSettings }] = useProfileSettingsCache();
  const hasPMEditAccess = profileSettings.userPermissionItemTypes.includes(PERMISSIONS.PIP_PM_EDIT);
  const hasPMDeleteAccess = profileSettings.userPermissionItemTypes.includes(PERMISSIONS.PIP_PM_DELETE);
  // Outcome of the acquire fired when this panel mounted (i.e. when the row was
  // expanded). Null until it returns, or on the tabs that don't lock at all.
  const [lockResult, setLockResult] = useState<PolicyTransactionLockResultDto | null>(
    null
  );
  // The server's answer wins once we have it: LockAcquired tells us definitively
  // whether we hold the lock. Until then fall back to the lock columns on the
  // grid row, which cover the first render. GUIDs are compared case-insensitively
  // because SQL and MSAL disagree on casing; a holder with no ID is treated as
  // someone else so an unknown state fails closed rather than open.
  //
  // LockAcquired:false alone doesn't say who the holder is, so also compare
  // the reported holder's name against the caller's — a stale self-lock the
  // backend treats as a conflict must never lock you out of your own record.
  // No ExistingLockedByUserID is returned, so this is a name match, not a GUID.
  const currentUserDisplayName = (profileSettings?.displayName ?? "").toLowerCase();
  const isLockedByOther = lockResult
    ? !lockResult.LockAcquired &&
      (lockResult.ExistingLockedByUserName ?? "").toLowerCase() !==
        currentUserDisplayName
    : !!record.LockedByUserName &&
      (!record.LockedByUserID ||
        record.LockedByUserID.toLowerCase() !==
          (profileSettings?.userID ?? "").toLowerCase()) &&
      record.LockedByUserName.toLowerCase() !== currentUserDisplayName;
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [financialsOpen, setFinancialsOpen] = useState(true);
  const [transactionOpen, setTransactionOpen] = useState(true);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<Opt[]>([
    SELECT_PLACEHOLDER,
  ]);

  useEffect(() => {
    let cancelled = false;
    getPropertyTypeList().then((types) => {
      if (cancelled) return;
      setPropertyTypeOptions([
        SELECT_PLACEHOLDER,
        ...types.map((t) => ({
          value: t.PropertyTypeCode,
          text: t.PropertyTypeName,
        })),
      ]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const recordTypeOptions = useRecordTypeOptions();
  const recordTypeSelectOptions = useMemo<Opt[]>(
    () => [
      SELECT_PLACEHOLDER,
      ...recordTypeOptions.map((o) => ({ value: o.value, text: o.label })),
    ],
    [recordTypeOptions]
  );

  const transactionTypeOptions = useTransactionTypeOptions();
  const transactionTypeSelectOptions = useMemo<Opt[]>(
    () => [
      SELECT_PLACEHOLDER,
      ...transactionTypeOptions.map((o) => ({ value: o.value, text: o.label })),
    ],
    [transactionTypeOptions]
  );

  // The drawer lives in a <td> that spans the full (often very wide) table, so
  // its content wouldn't reflow to the device — fields would render off-screen
  // to the right. Constrain it to the scroll container's visible width and pin
  // it to the left so the inner grids actually collapse on smaller screens.
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState<number>();
  useEffect(() => {
    const el = panelRef.current;
    const scroller = el?.closest(".MuiTableContainer-root") as HTMLElement | null;
    if (!scroller) return;
    const update = () => setPanelWidth(scroller.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);
  const status = getStatusBadge(record);
  const defaultValues = useMemo(() => toFormValues(record), [record]);

  const methods = useForm<FormValues>({
    resolver: yupResolver(policyDetailsSchema) as any,
    defaultValues: defaultValues as any,
    mode: "onBlur",
  });

  const { watch } = methods;

  const stateCode = watch("stateCode");

  const [hasUserEdits, setHasUserEdits] = useState(false);

  // Server validation errors for the currently open row, each paired with the
  // value the server rejected so the message can be tied to that value.
  const [serverFieldErrors, setServerFieldErrors] = useState<ServerFieldError[]>(
    []
  );

  const serverErrorFields = useMemo(
    () => new Set<string>(serverFieldErrors.map((e) => e.name as string)),
    [serverFieldErrors]
  );

  const lastEmittedRecordRef = useRef<PolicyManagementRecord | null>(null);
  const skipNextBookedRowSwapRef = useRef(true);

  // Reset form when a different row is opened.
  useEffect(() => {
    if (record === lastEmittedRecordRef.current) return;
    methods.reset(toFormValues(record));
    skipNextBookedRowSwapRef.current = true;
    setHasUserEdits(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    record.uniqueIdentifier,
    record.LastEditedDate,
    record.IsOverride,
    record.NewRemitSplitPct,
    record.ActualFee,
    record.ActualPremiumTax,
    record.ActualRemittance,
    record.CalculatedFee,
    record.CalculatedPremiumTax,
    record.CalculatedRemittance,
    record.SILiabilityAmount,
    record.AdditionalLiabilityAmount,
    record.ReissueLiabilityAmount,
  ]);

  // Surface per-field server validation errors (record.PolicyErrors) under the
  // matching field. Runs after the reset above so the injected errors survive.
  // Nothing validates on mount (mode is "onBlur"), so this setError is what
  // paints them initially; the reconcile effect below keeps them in step with
  // the values afterwards. The resolved names also drive the force-enable in
  // FieldCell, so a flagged field can be corrected in place.
  useEffect(() => {
    const validNames = new Set(Object.keys(defaultValues));
    const fieldErrors = (record.PolicyErrors ?? []).filter(
      (e) => e.Active !== 0 && !e.IsDeleted
    );
    const flagged: ServerFieldError[] = [];
    fieldErrors.forEach((e) => {
      if (!e.ErrorFieldName) return;
      const formName = resolveErrorFieldName(e.ErrorFieldName, validNames);
      if (formName) {
        const message = e.PolicyErrorExceptionMessage ?? "";
        flagged.push({
          name: formName,
          message,
          rejectedValue: (defaultValues as Record<string, unknown>)[formName],
        });
        methods.setError(formName, {
          type: "server",
          message,
        });
      }
    });
    setServerFieldErrors(flagged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.uniqueIdentifier, record.LastEditedDate, record.PolicyErrors]);

  // Bubble live edits back to the parent tab.
  useEffect(() => {
    const sub = methods.watch((values, { name }) => {
      // `name` is set for user-driven field changes and undefined for the
      // programmatic reset above, so this only flags genuine reviewer edits.
      if (name) setHasUserEdits(true);
      const merged = mergeBack(record, values as Partial<FormValues>);
      merged.PropertyTypeCode = resolveOptionValue(
        merged.PropertyTypeCode ?? "",
        propertyTypeOptionsRef.current
      );
      merged.RecordTypeCode = resolveOptionValue(
        merged.RecordTypeCode ?? "",
        recordTypeOptionsRef.current
      );
      lastEmittedRecordRef.current = merged;
      const selected = countyOptionsRef.current.find(
        (c) => c.countyCode === values.countyCode
      );
      if (selected) {
        merged.CountyDisplayName = `${selected.countyCode} - ${selected.countyName}`;
      }
      onRecordChange?.(merged);
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods, record.uniqueIdentifier, onRecordChange]);

  // Auto-lock for the duration of the editing session. This panel is only
  // mounted while a QR/Pre-Close row is expanded (see the conditional
  // renderRowDetails in QualityReviewTable/PreCloseTable), so mount means
  // "expanded" and leaving the session covers both "user collapses the row" and
  // "user navigates away without collapsing" — both simply unmount this
  // component.
  //
  // Acquire unconditionally and let the response decide whether we may edit: the
  // grid row's lock columns can be stale, and the server is the only authority.
  //
  // The session (shared/lock.ts) is what keeps this to one acquire per expand
  // and one release per collapse: StewartTable can rebuild the expanded details
  // row, so this effect may run more than once for a single expand, and the
  // ref-counted session collapses those into a single pair of calls. It also
  // owns the decision to release — only if we actually got the lock, so a viewer
  // who was refused can never release the holder's lock on their way out.
  useEffect(() => {
    if (!validationLocked) return;
    const identityKey = record.PolicyTransactionID;
    let cancelled = false;
    beginLockSession(identityKey).then((result) => {
      if (cancelled) return;
      setLockResult(result);
    });
    return () => {
      cancelled = true;
      endLockSession(identityKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationLocked, record.PolicyTransactionID]);
  const bookedRow = methods.watch("bookedRow");
  const propertyTypeValue = methods.watch("propertyTypeCode");
  const recordTypeValue = methods.watch("recordTypeCode");

  useEffect(() => {
    if (skipNextBookedRowSwapRef.current) {
      skipNextBookedRowSwapRef.current = false;
      return;
    }
    if (!record.IsOverride) return;
    const financials = financialsDisplaySource(record, bookedRow);
    methods.setValue("reportedPremium", financials.reported.fee ?? 0);
    methods.setValue("reportedTax", financials.reported.tax ?? 0);
    methods.setValue("reportedUwRemittance", financials.reported.remittance ?? 0);
    methods.setValue("calculatedFee", financials.calculated.fee ?? 0);
    methods.setValue("calculatedTax", financials.calculated.tax ?? 0);
    methods.setValue("calculatedUwRemittance", financials.calculated.remittance ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookedRow]);

  const { errors: formErrors } = useFormState({ control: methods.control });
  const canRerunValidation = hasUserEdits && Object.keys(formErrors).length === 0;

  const flaggedNames = useMemo(
    () => serverFieldErrors.map((e) => e.name as string),
    [serverFieldErrors]
  );
  // Re-render this panel whenever a flagged field's value changes, so the
  // reconcile below gets a chance to run. An empty list subscribes to nothing.
  useWatch({ control: methods.control, name: flaggedNames as any });

  // A server error belongs to the value that produced it, so keep it attached
  // for exactly as long as the field still holds that value. Without this, a
  // blur silently drops it: RHF re-runs the resolver on blur and the schema has
  // no rule of its own for these fields, and DatePickerField/SelectField clear
  // errors on blur of their own accord. Reverting an edit brings it back.
  //
  // No dependency array on purpose — RHF mutates its errors object in place and
  // hands back the same reference, so `formErrors` can't be used as a dep.
  // useFormState already re-renders whenever an error is added or removed, and
  // this body is idempotent.
  useEffect(() => {
    serverFieldErrors.forEach(({ name, message, rejectedValue }) => {
      const stillRejected = isSameFieldValue(
        methods.getValues(name),
        rejectedValue
      );
      const current = (formErrors as Record<string, any>)[name as string];
      if (stillRejected && !current) {
        methods.setError(name, { type: "server", message });
      } else if (!stillRejected && current?.type === "server") {
        methods.clearErrors(name);
      }
    });
  });

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [removeHoldOpen, setRemoveHoldOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pushBackOpen, setPushBackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const { stateList } = useStateList();
  const TransCodeList = useTransCodeOptions();
  const countyOptions = useCountyList([stateCode]);
  const countyOptionsRef = useRef(countyOptions);
  countyOptionsRef.current = countyOptions;

  const transCodeOptions = useMemo<Opt[]>(
    () => TransCodeList.map((s) => ({ value: s.value, text: s.value })),
    [TransCodeList]
  );

  const propertyTypeOptionsRef = useRef(propertyTypeOptions);
  propertyTypeOptionsRef.current = propertyTypeOptions;
  const recordTypeOptionsRef = useRef(recordTypeSelectOptions);
  recordTypeOptionsRef.current = recordTypeSelectOptions;

  const [histEditMode, setHistEditMode] = useState(false);
  const histEditSnapshotRef = useRef<Record<string, string> | null>(null);

  const HIST_EDITABLE_FIELDS: Record<string, string> = {
    municipalityNum: "Municipality",
    countyCode: "County",
    propertyTypeCode: "Property Type",
  };

  const toggleHistEditMode = () => {
    if (!histEditMode) {
      histEditSnapshotRef.current = Object.keys(HIST_EDITABLE_FIELDS).reduce(
        (acc, name) => ({ ...acc, [name]: methods.getValues(name as any) }),
        {} as Record<string, string>
      );
      setHistEditMode(true);
      return;
    }
  };

  const editHistoryRecord = async () => {
    if (!histEditMode) return;

    const editDto: EditPolicyHistoryDto = {
      PolicyHistoryID: record.PolicyHistoryID!,
      MunicipalityNum: methods.getValues("municipalityNum"),
      CountyCode: methods.getValues("countyCode"),
      PropertyTypeCode: methods.getValues("propertyTypeCode"),
    };

    await savePolicyHistory(editDto);

    showToast({
      title: "History record updated successfully.",
      type: "success",
    })

    setHistEditMode(false);
  };

  const historyFieldDisabled = !histEditMode;

  const hasEditedFields = !!(record.EditedFields && record.EditedFields.length > 0);

  const hasOpenErrors = useMemo(
    () =>
      (record.errors ?? []).some(
        (e) => e.policyErrorStatusTypeCode === "OPEN"
      ),
    [record.errors]
  );

  // No force-release endpoint yet — ReleasePolicyTransactionLock only ever
  // releases the caller's own lock. Stub until that endpoint exists.
  const handleUnlockRecord = () => {
    console.log("Unlock Record — not yet implemented", record.PolicyTransactionID);
  };

  const handleBulkEditPolicyTransaction = async (changes: { key: string, value: string }[], notes: string) => {

    const editedRecordChanges = changes.map(c => ({
      key: EDIT_FIELD_BY_KEY[c.key]?.apiKey ?? c.key,
      value: c.value,
    }));

    const keyValueFields = Object.fromEntries(
      editedRecordChanges.map(c => [c.key, c.value])
    );


    const bulkEditPayload = {
      PolicyTransactionIDList: record.uniqueIdentifier,
      ...(notes ? { NoteText: notes } : {}),
      ...keyValueFields,
    }

    const response = await bulkEditPolicyTransaction(bulkEditPayload);
    if (response) {
      showToast({
        type: "success",
        message: `Record ${record.PolicyNumber} updated successfully.`,
      });
      onRefresh?.();
      navigate("/policyManagement");
    }
    else {
      showToast({
        type: "error",
        message: `Record ${record.PolicyNumber} failed to update.`,
      });
    }

  }

  // Resolves a field's disabled state identically for both branches — the
  // standalone Product Details page is a straight replica of the drawer's
  // enabled/disabled fields (driven by `editable`/`isReadOnly`), not a
  // separate restricted-editing model. A couple of fields (County/
  // Municipality/Property Type) are additionally gated by
  // historyFieldDisabled on the History tab.
  const fieldDisabled = (
    fieldKey: keyof FormValues,
    opts?: { historyAware?: boolean }
  ) => {
    if (opts?.historyAware)
      return isLockedByOther || (variant === "history" ? historyFieldDisabled : isReadOnly);
    return isLockedByOther || isReadOnly;
  };

  // Shared Policy / Transaction field grids — the same fields/layout for
  // both the standalone Product Details page (hideActions) and the
  // queue-table expanded-row drawer. Each branch supplies its own section
  // wrapper (collapsible "Details" grouping vs. standalone "Policy"/
  // "Transaction" sections) around this shared content.
  const policyFieldsGrid = (
    <Grid5>
      <FieldCell>
        <PanelTextField name="policyNumber" label="Policy Number" disabled />
      </FieldCell>
      <FieldCell>
        <PanelTextField name="fileNumber" label="File Name/Number" disabled />
      </FieldCell>
      <FieldCell>
        <PanelTextField name="reportingId" label="Reporting ID" disabled />
      </FieldCell>
      <FieldCell>
        <PanelTextField name="agentID" label="Agent ID" disabled />
      </FieldCell>
      <FieldCell>
        <PanelTextField name="agentName" label="Agency Location" disabled />
      </FieldCell>
      <PanelSelectField
        name="stateCode"
        label="State"
        optionsTextField="value"
        optionsValueField="value"
        options={stateList.map((s) => ({ value: s.value, text: s.value }))}
        disabled={fieldDisabled("stateCode")}
      />
      <PanelTextField name="city" label="City" disabled={fieldDisabled("city")} />
      <PanelTextField name="zip" label="Zip" disabled={fieldDisabled("zip")} />
      <FieldCell>
        <PanelSelectField
          name="countyCode"
          label="County"
          optionsTextField="label"
          optionsValueField="value"
          options={countyOptions.map((c) => ({ value: c.countyCode, label: `${c.countyCode} - ${c.countyName}` }))}
          disabled={fieldDisabled("countyCode", { historyAware: true })}
        />
      </FieldCell>
      <PanelTextField
        name="municipalityNum"
        label="Municipality"
        disabled={fieldDisabled("municipalityNum", { historyAware: true })}
      />
      <DatePickerField
        name="effectiveDate"
        label="Effective Date"
        disabled={fieldDisabled("effectiveDate")}
      />
      <DatePickerField name="issueDate" label="Issue Date" disabled />
      <DatePickerField name="voidedDate" label="Voided Date" disabled />
      <DatePickerField name="receivedDate" label="Received Date" disabled />
    </Grid5>
  );

  const transactionFieldsGrid = (
    <>
      <Grid5>
        <FieldCell>
          <PanelSelectField
            name="transactionType"
            label="Transaction Type"
            options={transactionTypeSelectOptions}
            disabled={fieldDisabled("transactionType")}
          />
        </FieldCell>
        <FieldCell>
          <PanelSelectField
            name="propertyTypeCode"
            label="Property Type"
            options={propertyTypeOptions}
            overrideValue={resolveOptionValue(
              propertyTypeValue ?? "",
              propertyTypeOptions
            )}
            disabled={fieldDisabled("propertyTypeCode", { historyAware: true })}
            errorOverride={
              hideActions && !propertyTypeValue
                ? "Property Type is required to clear validation"
                : undefined
            }
          />
        </FieldCell>
        <FieldCell>
          <PanelTextField name="productName" label="Policy Type" disabled />
        </FieldCell>
        <FieldCell>
          <PanelTextField name="policyFormName" label="Form Name" disabled />
        </FieldCell>
        <PanelSelectField
          name="recordTypeCode"
          label="Record Type"
          options={recordTypeSelectOptions}
          overrideValue={resolveOptionValue(
            recordTypeValue ?? "",
            recordTypeSelectOptions
          )}
          disabled={fieldDisabled("recordTypeCode")}
        />
        <PanelTextField
          name="originalPolicyNumber"
          label="Original Policy Number"
          disabled={fieldDisabled("originalPolicyNumber")}
        />
        <DatePickerField
          name="originalPolicyDate"
          label="Original Policy Date"
          disabled={fieldDisabled("originalPolicyDate")}
        />
        <PanelTextField
          name="originalPolicyType"
          label="Original Policy Type"
          disabled={fieldDisabled("originalPolicyType")}
        />
        <NumberInputFieldPricing
          name="originalPolicyAmount"
          label="Original Policy Amount"
          disabled={fieldDisabled("originalPolicyAmount")}
        />
        <Box sx={{ gridColumnStart: 1 }}>
          <FieldCell>
            <NumberInputFieldPricing name="liabilityAmount" label="Liability Amount" disabled />
          </FieldCell>
        </Box>
        <FieldCell>
          <NumberInputFieldPricing name="reissueLiabilityAmount" label="Reissue Liability" disabled />
        </FieldCell>
        <FieldCell>
          <NumberInputFieldPricing name="siLiabilityAmount" label="SI Liability" disabled />
        </FieldCell>
        <FieldCell>
          <NumberInputFieldPricing name="addlLiabilityAmount" label="Additional Liability" disabled />
        </FieldCell>
      </Grid5>
      {/* Trans Codes 1-8, each paired side-by-side with its Rate Amount */}
      <Grid8>
        {([1, 2, 3, 4, 5, 6, 7, 8] as const).map((n) => {
          const codeName = (n === 1 ? "transCode" : `transCode${n}`) as keyof FormValues;
          const amountName = (n === 1 ? "rateAmount" : `rateAmount${n}`) as keyof FormValues;
          const codeValue = methods.watch(codeName);
          return (
            <TransCodePair key={n}>
              <FieldCell>
                <PanelSelectField
                  name={codeName}
                  label={`Trans Code ${n}`}
                  optionsTextField="value"
                  optionsValueField="value"
                  options={optionsWithValue(transCodeOptions, codeValue)}
                  overrideValue={resolveOptionValue(codeValue, transCodeOptions)}
                  disabled={fieldDisabled(codeName)}
                />
              </FieldCell>
              <NumberInputFieldPricing
                name={amountName}
                label={`Amount ${n}`}
                disabled={fieldDisabled(amountName)}
              />
            </TransCodePair>
          );
        })}
      </Grid8>
    </>
  );

  // Shared action row + modals — reused by both the standalone Product
  // Details page (hideActions) and the queue-table expanded-row drawer, so
  // Force Promote/Hold/Transfer/Override Financials/Re-run Validation behave
  // identically (and contend on the same lock) regardless of where they're
  // triggered from.
  const recordActionsRow = (
    <>
      {variant === "cq" ? (
        <CqBanner>
          <InfoOutlinedIcon sx={{ color: "#c49a24", fontSize: 20 }} />
          <Box component="span" sx={{ fontSize: fontSize.small }}>
            <strong>Read-only at this stage.</strong> To change which row is
            booked, push this record back to Pre-Close Queue.
          </Box>
          <Box sx={{ marginLeft: "auto" }}>
            <ActionButton
              variant="outlined"
              color="secondary"
              startIcon={<UndoIcon />}
              disabled={isLockedByOther}
              onClick={() => setPushBackOpen(true)}
            >
              Push Back to Pre-Close
            </ActionButton>
          </Box>
        </CqBanner>
      ) : variant === "history" ? (
        <ActionRow>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: gaps.xsmall1,
              fontSize: fontSize.xsmall,
              color: colors.grey11,
            }}
          >
            <HistoryIcon sx={{ fontSize: 16 }} />
            <span>
              Closed {formatDate(record.closedDate)} by{" "}
              {record.closedByUserID || "—"}
            </span>
          </Box>
          <Box sx={{ marginLeft: "auto", display: "flex", gap: gaps.small1 }}>
            <ActionButton
              variant="outlined"
              color="secondary"
              startIcon={<PublishedWithChangesIcon />}
              disabled={isLockedByOther}
              onClick={() => {
                if (record?.AdjustmentDate) {
                  showToast({
                    type: "info",
                    message: `Already Adjusted (reversal only). See ${record?.PolicyNumber} in Close Queue.`
                  })
                } else {
                  setAdjustOpen(true)
                }
              }}
            >
              Adjust
            </ActionButton>
            <ActionButton
              variant="outlined"
              color="secondary"
              startIcon={<VisibilityOutlinedIcon />}
              disabled={isLockedByOther}
              onClick={() =>
                console.log("Open Full Details", record.uniqueIdentifier)
              }
            >
              Open Full Details
            </ActionButton>
          </Box>
        </ActionRow>
      ) : (
        <ActionRow>
          <ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<LockOutlinedIcon />}
            disabled={isLockedByOther}
            onClick={() => setOverrideOpen(true)}
          >
            Override Financials
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<WarningAmberIcon />}
            disabled={isLockedByOther || (record.IsOnHold && !record.IsForcePromoted)}
            onClick={() => {
              if (record.IsForcePromoted) {
                removeForcePromoteRecord(
                  Number(record.PolicyTransactionID)
                ).then((ok) => {
                  if (ok) {
                    forceReleaseLockSession(record.PolicyTransactionID);
                    onRefresh?.();
                  }
                });
              } else {
                setFpOpen(true);
              }
            }}
          >
            {record.IsForcePromoted
              ? "Remove Force Promoted"
              : isPlainPromote
              ? "Promote"
              : "Force Promote"}
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<PauseCircleOutlineIcon />}
            disabled={isLockedByOther}
            onClick={() => {
              if (record.IsOnHold) {
                setRemoveHoldOpen(true);
              } else {
                setHoldOpen(true);
              }
            }}
          >
            {record.IsOnHold ? "Remove Hold" : "Hold"}
          </ActionButton>
          {hasPMEditAccess && (<ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<SwapHorizIcon />}
            disabled={isLockedByOther}
            onClick={() => setTransferOpen(true)}
          >
            Transfer
          </ActionButton>)}
          {hasPMDeleteAccess && (<ActionButton
            variant="outlined"
            color="secondary"
            startIcon={<DeleteOutlineIcon />}
            disabled={isLockedByOther}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </ActionButton>)}

          <ActionButton
            variant="outlined"
            color="primary"
            startIcon={<AutorenewIcon />}
            disabled={isLockedByOther || !canRerunValidation}
            onClick={() => {
              forceReleaseLockSession(record.PolicyTransactionID);
              console.log("Re-run Validation Rules", record.uniqueIdentifier);
            }}
          >
            Re-run Validation Rules
          </ActionButton>
        </ActionRow>
      )}
    </>
  );

  const recordActionModals = (
    <>
      <OverrideFinancialsModal
        open={overrideOpen}
        onClose={() => {
          setOverrideOpen(false);
          forceReleaseLockSession(record.PolicyTransactionID);
        }}
        onRefresh={onRefresh}
        isReported={bookedRow === BookedRow.REPORTED}
        record={record}
      />
      <ForcePromoteModal
        open={fpOpen}
        onClose={() => setFpOpen(false)}
        record={record}
        destinationLabel={variant === "pcq" ? "Close Queue" : "Pre-Close Queue"}
        isForcePromote={!isPlainPromote}
        onSubmit={(reason) =>
          forcePromoteRecords(
            [Number(record.PolicyTransactionID)],
            variant === "pcq" ? StageType.CLOSE : StageType.PRECLOSE,
            reason,
            isPlainPromote ? 0 : 1
          ).then((ok) => {
            if (ok) {
              forceReleaseLockSession(record.PolicyTransactionID);
              onRefresh?.();
            }
          })
        }
      />
      <BulkActionModal
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        recordCount={1}
        tabName=""
        actionType={PIPActionType.BULK_HOLD}
        policyNumber={record.PolicyNumber ?? undefined}
        onConfirm={async (reason, holdScope) => {
          const ok = await holdPolicyRecord(
            String(record.PolicyTransactionID),
            reason,
            holdScope === HOLD_SCOPE.ALL_MATCHING
          );
          if (ok) {
            forceReleaseLockSession(record.PolicyTransactionID);
            onRefresh?.();
            onCollapse?.();
          }
          setHoldOpen(false);
        }}
      />
      <BulkActionModal
        open={removeHoldOpen}
        onClose={() => setRemoveHoldOpen(false)}
        recordCount={1}
        tabName=""
        actionType={PIPActionType.REMOVE_HOLD}
        policyNumber={record.PolicyNumber ?? undefined}
        infoMessage={
          <>
            Hold applied by{" "}
            <Box component="span" sx={{ fontWeight: fontWeight.bold2 }}>
              {record.LastHeldByUserName ?? "—"}
            </Box>{" "}
            on{" "}
            <Box component="span" sx={{ fontWeight: fontWeight.bold2 }}>
              {formatDate(record.LastOnHoldDate)}
            </Box>
            . The release will be logged to the activity log.
          </>
        }
        onConfirm={async (_reason, holdScope) => {
          const ok = await removeHoldPolicyRecord(
            String(record.PolicyTransactionID),
            holdScope === HOLD_SCOPE.ALL_MATCHING
          );
          if (ok) {
            onRefresh?.();
            onCollapse?.();
          }
          setRemoveHoldOpen(false);
        }}
      />
      <TransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        record={record}
        onSubmit={() => onRefresh?.()}
      />
      <BulkActionModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        recordCount={1}
        tabName=""
        actionType={PIPActionType.BULK_DELETE}
        policyNumber={record.PolicyNumber ?? undefined}
        onConfirm={async (reason) => {
          const ok = await deletePolicyRecord(String(record.PolicyTransactionID), reason);
          if (ok) {
            forceReleaseLockSession(record.PolicyTransactionID);
            onRefresh?.();
            onCollapse?.();
          }
          setDeleteOpen(false);
        }}
      />
      {/* Single-record push back: reuses the Close Queue bulk return modal and
          API call with this drawer's record as the only selected record. */}
      <BulkActionModal
        open={pushBackOpen}
        onClose={() => setPushBackOpen(false)}
        recordCount={1}
        tabName="CLOSE QUEUE"
        returnTabName="PRE CLOSE"
        actionType={PIPActionType.BULK_RETURN}
        onConfirm={async (reason) => {
          const ok = await handleBulkReturnPolicyTransaction(
            [String(record.PolicyTransactionID)],
            reason,
            StageType.PRECLOSE
          );
          if (ok) {
            setPushBackOpen(false);
            onRefresh?.();
            onCollapse?.();
          }
        }}
      />
      <AdjustmentModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onRefresh={onRefresh}
        record={record}
      />
    </>
  );

  if (hideActions) {
    const openErrors =
      record.errors?.filter((e) => e.policyErrorStatusTypeCode === "OPEN") ?? [];
    const openErrorCount = openErrors.length;
    const hasErrors = record.validationState === "errors";

    return (
      <>
        <ServerErrorFieldsContext.Provider value={serverErrorFields}>
          <FormProvider {...methods}>
            {/* DETAILS — wraps Policy Identity + Transaction; collapsible */}
            <Section>
              <SectionHeader
                sx={{
                  cursor: "pointer",
                  borderBottom: detailsOpen ? `1px solid ${colors.grey09}` : "none",
                }}
                onClick={() => setDetailsOpen((o) => !o)}
              >
                <SectionTitle>Details</SectionTitle>
                <Box
                  component="span"
                  sx={{
                    fontSize: fontSize.xsmall,
                    fontWeight: fontWeight.bold2,
                    borderRadius: borderRadius.xsmall,
                    padding: "2px 7px",
                    border: `1px solid ${status.border}`,
                    backgroundColor: status.bg,
                    color: status.color,
                    whiteSpace: "nowrap",
                  }}
                >
                  {status.label}
                </Box>
                <Box
                  sx={{ marginLeft: "auto" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ActionButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<EditOutlinedIcon />}
                    disabled={isLockedByOther}
                    onClick={() => {
                      lockPolicyRecord(record.PolicyTransactionID);
                      setEditModalOpen(true);
                    }}
                  >
                    Edit
                  </ActionButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {detailsOpen ? (
                    <ExpandLessIcon sx={{ color: colors.blue15 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: colors.blue15 }} />
                  )}
                </Box>
              </SectionHeader>
              <Collapse in={detailsOpen} unmountOnExit>
                {/* Validation banner — visible when validationState !== "passed".
                Red for open errors (per mockup), amber for force-promoted. */}
                {record.validationState !== "passed" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: gaps.small1,
                      marginTop: gaps.small1,
                      marginBottom: gaps.small1,
                      backgroundColor: hasErrors ? "#fdecea" : "#fff8e5",
                      border: hasErrors
                        ? "1px solid #f4c7c3"
                        : "1px solid #e8c068",
                      borderRadius: borderRadius.small,
                      padding: `${padding.small1} ${padding.medium1}`,
                      color: hasErrors ? "#b71c1c" : "#7a5a00",
                    }}
                  >
                    {hasErrors ? (
                      <ErrorOutlineIcon sx={{ color: colors.red02, fontSize: 20 }} />
                    ) : (
                      <WarningAmberIcon sx={{ color: "#c49a24", fontSize: 20 }} />
                    )}
                    <Box component="span" sx={{ flex: 1, fontSize: fontSize.small }}>
                      {hasErrors ? (
                        <>
                          <strong>
                            {openErrorCount} validation error
                            {openErrorCount === 1 ? "" : "s"}
                          </strong>{" "}
                          on this record. Fix the highlighted fields below, then
                          click Re-run Validation to advance the record.
                        </>
                      ) : (
                        <strong>
                          Force-promoted; validation pending Re-run Validation.
                        </strong>
                      )}
                    </Box>
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<AutorenewIcon />}
                      disabled={!canRerunValidation}
                      onClick={() =>
                        console.log(
                          "Re-run Validation (Details)",
                          record.uniqueIdentifier
                        )
                      }
                    >
                      Re-run Validation
                    </ActionButton>
                  </Box>
                )}

                {isLockedByOther && (
                  <LockedBanner
                    record={record}
                    lockedByUser={lockResult?.ExistingLockedByUserName}
                    lockedUntil={lockResult?.ExistingExpiresDate}
                  />
                )}

                {/* POLICY — reuses the same field grid as the expanded row's Policy section */}
                <SubSection>
                  <SubSectionTitle>Policy</SubSectionTitle>
                  {policyFieldsGrid}
                </SubSection>
              </Collapse>
            </Section>

            <Section>
              <SectionHeader
                sx={{
                  cursor: "pointer",
                  borderBottom: transactionOpen
                    ? `1px solid ${colors.grey09}`
                    : "none",
                }}
                onClick={() => setTransactionOpen((o) => !o)}
              >
                <SectionTitle>Transaction</SectionTitle>
                <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                  {transactionOpen ? (
                    <ExpandLessIcon sx={{ color: colors.blue15 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: colors.blue15 }} />
                  )}
                </Box>
              </SectionHeader>

              <Collapse in={transactionOpen} unmountOnExit>
                {transactionFieldsGrid}
              </Collapse>
            </Section>

            {/* FINANCIALS — collapsible; contains Reported + Calculated + Other */}
            <Section>
              <SectionHeader
                sx={{
                  cursor: "pointer",
                  borderBottom: financialsOpen
                    ? `1px solid ${colors.grey09}`
                    : "none",
                }}
                onClick={() => setFinancialsOpen((o) => !o)}
              >
                <SectionTitle>Financials</SectionTitle>
                <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                  {financialsOpen ? (
                    <ExpandLessIcon sx={{ color: colors.blue15 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: colors.blue15 }} />
                  )}
                </Box>
              </SectionHeader>

              <Collapse in={financialsOpen} unmountOnExit>
                {/* REPORTED */}
                <SubSection className={bookedRow === BookedRow.REPORTED ? "booked" : ""}>
                  <SubSectionTitle>
                    Reported
                    {bookedRow === BookedRow.REPORTED && (
                      <BookedPill>
                        <CheckCircleIcon sx={{ fontSize: 12 }} />
                        Booked
                      </BookedPill>
                    )}
                  </SubSectionTitle>
                  <Grid5>
                    <NumberInputFieldPricing
                      name="reportedPremium"
                      label="Premium/Fee"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="reportedRiskRate"
                      label="Risk Rate"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="reportedUwRemittance"
                      label="UW Remittance"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="reportedTax"
                      label="Tax / State Fee"
                      disabled
                    />
                  </Grid5>
                </SubSection>

                {/* CALCULATED */}
                <SubSection className={bookedRow === BookedRow.CALCULATED ? "booked" : ""}>
                  <SubSectionTitle>
                    Calculated
                    <SubSectionCaption>Sourced from STRAFE</SubSectionCaption>
                    {bookedRow === BookedRow.CALCULATED && (
                      <BookedPill>
                        <CheckCircleIcon sx={{ fontSize: 12 }} />
                        Booked
                      </BookedPill>
                    )}
                  </SubSectionTitle>
                  <Grid5>
                    <NumberInputFieldPricing
                      name="liabilityAmount"
                      label="Liability Amount"
                      disabled={isReadOnly}
                    />
                    <NumberInputFieldPricing
                      name="calculatedFee"
                      label="Premium/Fee"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="calculatedRiskRate"
                      label="Risk Rate"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="calculatedUwRemittance"
                      label="UW Remittance"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="calculatedTax"
                      label="Tax / State Fee"
                      disabled
                    />
                  </Grid5>
                </SubSection>

                {/* OTHER (moved inside Financials) */}
                <SubSection>
                  <SubSectionTitle>Other</SubSectionTitle>
                  <Grid4>
                    <NumberInputFieldPricing
                      name="originalPolicyAmount"
                      label="Original Policy Amount"
                      disabled={isReadOnly}
                    />
                    <NumberInputFieldPricing
                      name="reissueLiabilityAmount"
                      label="Reissue Liability"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="siLiabilityAmount"
                      label="SI Liability"
                      disabled
                    />
                    <NumberInputFieldPricing
                      name="addlLiabilityAmount"
                      label="Additional Liability"
                      disabled
                    />
                  </Grid4>
                </SubSection>

                {/* Override Comment — read-only; sourced from the record's override note. */}
                <SubSection>
                  <SubSectionTitle>Override Comment</SubSectionTitle>
                  <TextField
                    fullWidth
                    disabled
                    multiline
                    minRows={2}
                    value={record.OverrideNoteText ?? ""}
                    placeholder=""
                  />
                </SubSection>
              </Collapse>
            </Section>

            {recordActionsRow}
          </FormProvider>
        </ServerErrorFieldsContext.Provider>
        <EditRecordModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            unlockPolicyRecordSilently(record.PolicyTransactionID);
          }}
          record={record}
          onApply={async (changes, note) => {
            await handleBulkEditPolicyTransaction(changes, note);
          }}
        />
        {recordActionModals}
      </>
    );
  }

  return (
    <PanelContainer
      ref={panelRef}
      style={{ position: "sticky", left: 0, width: panelWidth }}
    >
      {
        variant === "history" && (
          <>
            {hasEditedFields && (
              <EditedAfterCloseBanner>
                <EditedAfterCloseHeader>
                  <WarningAmberIcon sx={{ fontSize: 20, marginTop: "2px" }} />
                  <EditedAfterCloseBody>
                    <Box component="span" sx={{ fontWeight: fontWeight.bold2 }}>
                      Edited after close - Connect file not updated
                    </Box>
                    <Box component="span">
                      One or more fields on this record have been corrected
                      after the close. The agent&apos;s Stewart Connect file
                      still reflects the original values and may be out of
                      sync with what is shown here. Limited-edits do not
                      change posted dollars or write back to Connect. If this
                      correction should change pricing, post an adjustment
                      record to reconcile.
                    </Box>
                  </EditedAfterCloseBody>
                  <ActionButton
                    variant="outlined"
                    color="secondary"
                    startIcon={histEditMode ? <CheckIcon /> : <EditOutlinedIcon />}
                    disabled={isLockedByOther}
                    onClick={histEditMode ? editHistoryRecord : toggleHistEditMode}
                  >
                    {histEditMode ? "Done Editing" : "Edit Record"}
                  </ActionButton>
                </EditedAfterCloseHeader>
                <EditedFieldsList>
                  {(record.EditedFields ?? []).map((f) => {
                    const eventAgo = relativeTime(f.EventDate);
                    return (
                      <EditedFieldRow key={f.PolicyAuditLogID}>
                        <Box component="span">
                          <strong>{editedFieldLabel(f.FieldName)}:</strong>{" "}
                          {f.OldValue ?? "—"} → <strong>{f.NewValue ?? "—"}</strong>
                        </Box>
                        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                          {f.ActingUser ?? "—"}
                          {eventAgo && ` - ${eventAgo}`}
                        </Box>
                      </EditedFieldRow>
                    );
                  })}
                </EditedFieldsList>
              </EditedAfterCloseBanner>
            )}
            {!hasEditedFields && (
              <HistCleanBanner>
                <HistoryIcon sx={{ color: colors.blue15, fontSize: 20 }} />
                <Box component="span" sx={{ flex: 1, fontSize: fontSize.small }}>
                  <strong>This record is closed.</strong> A limited set of fields —
                  Municipal Code, County Code, and Property Type — can be corrected
                  here. Dollars are not editable; pricing changes require an adjustment
                  record. Notes can be added on the Policy Details page.
                </Box>
                <ActionButton
                  variant="outlined"
                  color="secondary"
                  startIcon={histEditMode ? <CheckIcon /> : <EditOutlinedIcon />}
                  disabled={isLockedByOther}
                  onClick={histEditMode ? editHistoryRecord : toggleHistEditMode}
                >
                  {histEditMode ? "Done Editing" : "Edit Record"}
                </ActionButton>
              </HistCleanBanner>
            )}
            {record.AdjustmentKind && (
              <AdjustmentInfoBanner>
                <PublishedWithChangesIcon sx={{ color: colors.yellow01, fontSize: 20, marginTop: "2px" }} />
                <AdjustmentInfoBody>
                  <Box component="span" sx={{ fontWeight: fontWeight.bold2 }}>
                    This record has been adjusted
                    
                  </Box>

                  <Box component="span">
                    {record.AdjustmentDate && <strong>Adjusted on {formatDate(record.AdjustmentDate)}</strong>}
                    {record.AdjustmentByUserName && (
                      <>
                        {record.AdjustmentDate ? " by " : "Adjusted by "}
                        <strong>{record.AdjustmentByUserName}</strong>
                      </>
                    )}
                    {(record.AdjustmentDate || record.AdjustmentByUserName) && ". "}
                    
                      The original record below is preserved as-is; the dollar changes live on two new Close Queue records.
                  </Box>
                  {record.PolicyNumber != null && (
                    <Box sx={{ display: "flex", gap: gaps.large1, fontWeight: fontWeight.bold2 }}>
                      {record.AdjustmentKind === AdjustmentKind.REPLACEMENT ? (
                        <>
                          <span>Reversal: {record.PolicyNumber}</span>
                          <span>New Record: {record.PolicyNumber}</span>
                        </>
                      ) : (
                        <>
                          <span>Reversal: {record.PolicyNumber}</span>
                          <span style={{ fontStyle: "italic", fontWeight: fontWeight.normal1 }}>Reversal only - no replacement record created.</span>
                        </>
                      )}
                    </Box>
                  )}

                  <Box sx={{ width: "100%", margin: margin.xxsmall2, borderTop: `1px solid ${colors.yellow02}` }} />
                  <Box component="span">
                    {record.AdjustmentReasonNote && (
                      `Reason: ${record.AdjustmentReasonNote}`
                    )}
                  </Box>

                </AdjustmentInfoBody>
              </AdjustmentInfoBanner>
            )}
          </>
        )
      }
      <PanelContent>
        <ServerErrorFieldsContext.Provider value={serverErrorFields}>
          <FormProvider {...methods}>
            <FormFieldScope>
              {isLockedByOther && hasOpenErrors && (
                <LockedBanner
                  record={record}
                  lockedByUser={lockResult?.ExistingLockedByUserName}
                  lockedUntil={lockResult?.ExistingExpiresDate}
                  variant="beforeErrors"
                />
              )}
              {/* DRAWER LAYOUT — original flat sections, no collapse, no Details grouping */}
              {/* POLICY — shared with the hideActions (Product Details) branch */}
              <Section>
                <SectionHeader>
                  <SectionTitle>Policy</SectionTitle>
                  <Box
                    component="span"
                    sx={{
                      fontSize: fontSize.xsmall,
                      fontWeight: fontWeight.bold2,
                      borderRadius: borderRadius.xsmall,
                      padding: "2px 7px",
                      border: `1px solid ${status.border}`,
                      backgroundColor: status.bg,
                      color: status.color,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {status.label}
                  </Box>
                </SectionHeader>
                {policyFieldsGrid}
              </Section>

              {/* TRANSACTION — shared with the hideActions (Product Details) branch */}
              <Section>
                <SectionHeader>
                  <SectionTitle>Transaction</SectionTitle>
                </SectionHeader>
                {transactionFieldsGrid}
              </Section>

              {/* FINANCIALS (drawer) — Reported + Calculated only */}
              <Section>
                <SectionHeader>
                  <SectionTitle>Financials</SectionTitle>
                  <BookedSelector>
                    <BookedLabel>Booked</BookedLabel>
                    <RadioGroup
                      row
                      value={bookedRow}
                      sx={{
                        alignItems: "center",
                        "& .MuiFormControlLabel-root": { marginTop: 0, marginBottom: 0 },
                        "& .MuiRadio-root": { paddingTop: 0, paddingBottom: 0 },
                      }}
                      onChange={(_e, v) =>
                        methods.setValue("bookedRow", v as BookedRow, {
                          shouldDirty: true,
                        })
                      }
                    >
                      <FormControlLabel
                        value="calculated"
                        control={<Radio size="small" disabled={isLockedByOther || isReadOnly} />}
                        label="Calculated"
                      />
                      <FormControlLabel
                        value="reported"
                        control={<Radio size="small" disabled={isLockedByOther || isReadOnly} />}
                        label="Reported"
                      />
                    </RadioGroup>
                    <Chip
                      size="small"
                      label={`Original Split: ${record.OriginalRemitSplitPct != null
                        ? `${record.OriginalRemitSplitPct}%`
                        : "-"
                        }`}
                    />
                    <Chip
                      size="small"
                      label={`New Split: ${record.NewRemitSplitPct != null
                        ? `${record.NewRemitSplitPct}%`
                        : "-"
                        }`}
                    />
                  </BookedSelector>
                </SectionHeader>

                {/* REPORTED */}
                <SubSection className={bookedRow === BookedRow.REPORTED ? "booked" : ""}>
                  <SubSectionTitle>
                    Reported
                    {bookedRow === BookedRow.REPORTED && <BookedPill>Booked</BookedPill>}
                  </SubSectionTitle>
                  <Grid5>
                    <NumberInputFieldPricing
                      name="liabilityAmount"
                      label="Liability Amount"
                      disabled
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="reportedPremium"
                      label="Premium/Fee"
                      side={BookedRow.REPORTED}
                    />
                    <NumberInputFieldPricing
                      name="reportedRiskRate"
                      label="Risk Rate"
                      disabled={isLockedByOther || isReadOnly || bookedRow !== BookedRow.REPORTED}
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="reportedUwRemittance"
                      label="UW Remittance"
                      side={BookedRow.REPORTED}
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="reportedTax"
                      label="Tax / State Fee"
                      side={BookedRow.REPORTED}
                    />
                  </Grid5>
                </SubSection>

                {/* CALCULATED */}
                <SubSection className={bookedRow === BookedRow.CALCULATED ? "booked" : ""}>
                  <SubSectionTitle>
                    Calculated
                    {bookedRow === BookedRow.CALCULATED && <BookedPill>Booked</BookedPill>}
                  </SubSectionTitle>
                  <Grid5>
                    <NumberInputFieldPricing
                      name="liabilityAmount"
                      label="Liability Amount"
                      disabled
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="calculatedFee"
                      label="Premium/Fee"
                      side={BookedRow.CALCULATED}
                    />
                    <NumberInputFieldPricing
                      name="calculatedRiskRate"
                      label="Risk Rate"
                      disabled={isLockedByOther || isReadOnly || bookedRow !== BookedRow.CALCULATED}
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="calculatedUwRemittance"
                      label="UW Remittance"
                      side={BookedRow.CALCULATED}
                    />
                    <FinField
                      record={record}
                      bookedRow={bookedRow}
                      name="calculatedTax"
                      label="Tax / State Fee"
                      side={BookedRow.CALCULATED}
                    />
                  </Grid5>
                </SubSection>
              </Section>

              {isLockedByOther && (
                <LockedBanner
                    record={record}
                    lockedByUser={lockResult?.ExistingLockedByUserName}
                    lockedUntil={lockResult?.ExistingExpiresDate}
                    onUnlockClick={handleUnlockRecord}
                  />
              )}

              {recordActionsRow}
            </FormFieldScope>
          </FormProvider>
        </ServerErrorFieldsContext.Provider>
      </PanelContent>

      {recordActionModals}
    </PanelContainer>
  );
};

export default PolicyDetailsPanel;
